// FAQ section — shown on the landing state (no teaser, no results, not in
// sample mode). Designed to:
//   1) reduce pre-purchase friction for a $1 transactional product
//   2) capture long-tail SEO queries
//   3) feed FAQPage JSON-LD (declared in public/index.html) so AI agents
//      (ChatGPT / Perplexity / Claude) can cite us when users ask these
//      questions out-of-app
//
// To keep the JSON-LD in sync with the rendered list, the FAQ_ITEMS array
// here MUST match the schema declared in public/index.html. If you change
// one, change the other.
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

export const FAQ_ITEMS = [
  {
    id: 'data-sources',
    q: 'Where does the data come from?',
    a: (
      <>
        Every report draws on three live open-source data streams at runtime:
        <strong> company disclosures</strong>, <strong>quarterly financial and segment data</strong>,
        and <strong>recent macro/event context from the prior 14 days</strong>. The LLM is
        instructed to anchor every argument to retrieved source material, not to its own
        training data.
      </>
    ),
  },
  {
    id: 'credibility-audit',
    q: 'How is the credibility audit scored?',
    a: (
      <>
        Each of the 5 bull arguments is matched 1-on-1 against each of the 5 bear
        arguments — 25 head-to-head matchups in total. Each matchup is decided on the
        most decisive of four dimensions: <em>source primacy</em> (10-K {'>'} press release {'>'} blog),
        <em> recency</em>, <em>magnitude–evidence fit</em>, or <em>counterfactual specificity</em>.
        Each dimension must decide at least 4 matchups, and a decisiveness score (1–3) is
        attached to every result. The side with more wins is named the more credible.
      </>
    ),
  },
  {
    id: 'how-long',
    q: 'How long does a report take?',
    a: (
      <>
        Most reports complete in <strong>45–75 seconds</strong>. The pipeline runs the
        search and filings retrieval in parallel, then synthesizes the analysis, then runs
        the credibility audit and an adversarial fact-check pass. You can leave the tab
        open or come back to it — the job continues server-side.
      </>
    ),
  },
  {
    id: 'failure-mode',
    q: 'What happens if a report fails?',
    a: (
      <>
        <strong>You are not charged for failed reports.</strong> The $1 charge happens
        only after the analysis and audit complete successfully and you unlock the full
        version through Stripe. If anything fails server-side, you'll see a clear error
        and a one-click retry — no money moves.
      </>
    ),
  },
  {
    id: 'subscription',
    q: 'Is the $1 a subscription?',
    a: (
      <>
        No. It is <strong>$1 per report</strong>, charged once. No subscription, no
        auto-renewal, no stored card. The full report is yours to download as plain text
        and use however you like.
      </>
    ),
  },
  {
    id: 'data-freshness',
    q: 'How recent is the data?',
    a: (
      <>
        The macro / event search is constrained to the <strong>last 14 days</strong>, so
        the geopolitical context is always current at the time of generation. Company
        financials come from the most recently filed 10-K or 10-Q. Each report is
        timestamped at the top so you always know when it was generated.
      </>
    ),
  },
  {
    id: 'share-republish',
    q: 'Can I share or republish the report?',
    a: (
      <>
        Yes — the report is yours. Internal use, client memos, investor updates, social
        posts, anything. We just ask that public republication include attribution to
        GeoBull / GeoBear. Reports are not stored long-term on our servers; download
        the .txt file or copy to clipboard to keep your copy.
      </>
    ),
  },
  {
    id: 'who-built',
    q: 'Who built this?',
    a: (
      <>
        Built by <strong>Patrick Fruchet</strong> (Fruchet Consulting LLC, New York City),
        with the goal of giving a one-minute, evidence-grounded answer to one question:
        <em> how does this geopolitical event hit this stock?</em>
      </>
    ),
  },
];

export default function FAQ() {
  return (
    <section
      className="mt-16 max-w-3xl mx-auto"
      aria-labelledby="faq-heading"
      data-testid="faq-section"
    >
      <h2
        id="faq-heading"
        className="text-center font-serif tracking-tight"
        style={{
          color: 'hsl(var(--gb-navy))',
          fontSize: '1.75rem',
          marginBottom: '0.5rem',
        }}
      >
        Frequently asked questions
      </h2>
      <p
        className="text-center text-sm mb-8"
        style={{ color: 'hsl(var(--gb-text-muted))' }}
      >
        Everything you might want to know before generating your first report.
      </p>

      <Accordion
        type="single"
        collapsible
        className="w-full"
        data-testid="faq-accordion"
      >
        {FAQ_ITEMS.map((item) => (
          <AccordionItem
            key={item.id}
            value={item.id}
            data-testid={`faq-item-${item.id}`}
            style={{ borderColor: 'hsl(var(--gb-gold) / 0.35)' }}
          >
            <AccordionTrigger
              className="text-left text-base font-medium"
              style={{ color: 'hsl(var(--gb-navy))' }}
              data-testid={`faq-trigger-${item.id}`}
            >
              {item.q}
            </AccordionTrigger>
            <AccordionContent
              className="text-sm leading-relaxed"
              style={{ color: 'hsl(var(--gb-text))' }}
              data-testid={`faq-content-${item.id}`}
            >
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
